import inventory from '@/api/inventory-list'
import branch from '@/api/branch'
import location from '@/api/warehouse'
import moment from 'moment'
import { mapKeys, camelCase } from 'lodash'
import { baseState, baseMutations } from '@/store/mixins'

class Pagination {
  constructor(paging = {}) {
    this.currentPage = paging.currentPage ? parseInt(paging.currentPage) : 1
    this.lastPage = paging.lastPage ? parseInt(paging.lastPage) : 0
    this.perPage = paging.perPage ? parseInt(paging.perPage) : 15
    this.total = paging.total ? parseInt(paging.total) : 0
    this.from = this.perPage * (this.currentPage - 1) + 1
    this.to = this.perPage * this.currentPage
  }
}

export const state = {
  ...baseState,
  fullResources: [],  // Cache complete dataset for local search
  branch_id: null,
  location_id: null,
  date_from: moment().startOf('M').format('DD-MM-YYYY'),
  date_to: moment().endOf('M').format('DD-MM-YYYY')
}

export const getters = {
  // Filter fullResources based on search term
  filteredResources: (state) => {
    if (!state.search || state.search.trim() === '') {
      return state.fullResources
    }

    const searchTerm = state.search.toLowerCase()

    return state.fullResources.filter(item => {
      return (
        (item.category && item.category.toLowerCase().includes(searchTerm)) ||
        (item.location_name && item.location_name.toLowerCase().includes(searchTerm)) ||
        (item.barcode && item.barcode.includes(searchTerm)) ||
        (item.product_name && item.product_name.toLowerCase().includes(searchTerm)) ||
        (item.uom_name && item.uom_name.toLowerCase().includes(searchTerm))
      )
    })
  },

  // Apply pagination to filtered results
  paginatedResources: (state, getters) => {
    const filtered = getters.filteredResources
    const { currentPage, perPage } = state.pagination
    const start = (currentPage - 1) * perPage
    const end = start + perPage
    const lastPage = Math.max(1, Math.ceil(filtered.length / perPage))

    return {
      data: filtered.slice(start, end),
      current_page: currentPage,
      per_page: perPage,
      total: filtered.length,
      last_page: lastPage,
      from: filtered.length > 0 ? start + 1 : 0,
      to: Math.min(end, filtered.length)
    }
  }
}

export const mutations = {
  ...baseMutations,
  RECEIVE_RESOURCES(state, records) {
    const { data, ...meta } = records
    state.resources = data
    state.pagination = new Pagination(mapKeys(meta, (v, k) => camelCase(k)))
  },
  SET_DATE_FROM(state, date) {
    state.date_from = date
  },
  SET_DATE_TO(state, date) {
    state.date_to = date
  },
  SET_BRANCH_ID(state, branch_id) {
    state.branch_id = branch_id
  },
  SET_LOCATION_ID(state, location_id) {
    state.location_id = location_id
  },
  SET_FULL_RESOURCES(state, data) {
    state.fullResources = data
  },
  CLEAR_FULL_RESOURCES(state) {
    state.fullResources = []
  }
}

export const actions = {
  fetch(context, attrubutes = {}) {
    let params = Object.assign(
      {
        page: -1,  // Fetch all pages for local search
        perPage: context.state.pagination.perPage,
        // Removed: search parameter (filtering done locally)
        date_from: context.state.date_from,
        date_to: context.state.date_to,
        branch_id: context.state.branch_id,
        location_id: context.state.location_id
      },
      attrubutes
    )
    return inventory.get({ params }).then(response => {
      // Store the full dataset for local filtering
      context.commit('SET_FULL_RESOURCES', response.data.data || response.data)

      // Apply local pagination to filtered results
      const paginatedData = context.getters.paginatedResources
      context.commit('RECEIVE_RESOURCES', paginatedData)

      return Promise.resolve(response.data)
    })
  },
  applyLocalPagination(context) {
    const paginatedData = context.getters.paginatedResources
    context.commit('RECEIVE_RESOURCES', paginatedData)
  },
  store(context) {
    return inventory.store({
      search: context.state.search,
      date_from: context.state.date_from,
      date_to: context.state.date_to,
      branch_id: context.state.branch_id,
      location_id: context.state.location_id
    })
  },
  getBranch(context, attributes = {}) {
    return branch.get({
      params: {
        fields: {
          setup_branch_profiles: 'branch_id,branch_name_en'
        },
        page: -1
      },
      headers: {
        'X-PERMISSION-ALLOW-CODE': context.rootGetters.permissionAllowCode
      }
    })
  },
  getLocation(context, attributes = {}) {
    return location.get({
      params: attributes,
      headers: {
        'X-PERMISSION-ALLOW-CODE': context.rootGetters.permissionAllowCode
      }
    })
  }
}
